import React, { useState, useEffect, useRef } from 'react';

// =====================================================================================
// ===== MINIMIZABLE COMPOSE WINDOW - FLUJO CORREGIDO PARA GENERACIÓN DE IA ============
// =====================================================================================
// 
// CAMBIOS IMPLEMENTADOS PARA RESOLVER LOS PROBLEMAS:
// 
// PROBLEMA 1: Popup de eventos detectados aparece prematuramente
// SOLUCIÓN: El robotito verde (showCompletionTick) ahora es el único disparador
//           para iniciar el flujo de revisión. Los modales NO se muestran automáticamente.
// 
// PROBLEMA 2: Generación se queda estancada al cambiar de paciente
// SOLUCIÓN: La generación puede completarse en segundo plano. Cuando el usuario
//           vuelve y hace clic en el robotito verde, el flujo se completa correctamente.
// 
// FLUJO CORREGIDO:
// 1. Backend termina generación → AIEmailGenerator recibe lastSSEData.status = 'completed'
// 2. Frontend prepara datos → setPendingReviewData() (NO muestra modales)
// 3. Robotito se pone verde → setShowCompletionTick(true)
// 4. Usuario hace clic en robotito → handleAiDialogToggle() detecta showCompletionTick
// 5. Modal se abre/restaura → setAiDialogOpen(true), setAiDialogMinimized(false)
// 6. useEffect en AIEmailGenerator se dispara → Detecta !isCurrentlyMinimized && isGenerationCompleted
// 7. Flujo de revisión inicia → startReviewFlow() o setSuccessPending(true)
// 8. Modales se muestran → Solo cuando usuario está activamente viendo la pantalla
// 9. Flujo se completa → Snackbar de éxito y aplicación del email
// 
// =====================================================================================
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  Button,
  Alert,
  Chip,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
  Snackbar,
  styled,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull as OpenInFullIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  SmartToy as AIIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Fullscreen as FullscreenIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
  Lightbulb as LightbulbIcon,
  SmartToy as RobotIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  CropSquare as CropSquareIcon,
  Restore as RestoreIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import RichTextEditor from '../RichTextEditor';
import { Email } from '../../types/email';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import AIEmailGenerator from './AIEmailGenerator';
import { GenerationOptions } from '../../types/ai';

// Interfaces que ya conocemos del modal anterior
interface ComposeContext {
  mode: 'new' | 'reply' | 'reply-all' | 'forward';
  email?: Email | null;
  prefilledTo?: string;
  prefilledCc?: string;
  originalMode?: 'new' | 'reply' | 'reply-all' | 'forward' | 'edit-draft' | 'send-draft' | 'ai-generate';
  preparedBody?: string;
  pacienteData?: any;
  emailDetails?: any;
}

interface EmailPayload {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
}

// Props que necesitará nuestra nueva ventana
export interface MinimizableComposeWindowProps {
  composeId: string; // ID único para esta ventana
  initialContext: ComposeContext;
  onClose: (id: string) => void; // Para que el padre la elimine
  onSendSuccess: () => void;
  zIndex: number; // Para apilar ventanas correctamente
  rightOffset: number; // Para posicionar ventanas una al lado de la otra
  selectedPaciente?: any; // Paciente seleccionado desde el componente padre
  onWindowStateChange?: (composeId: string, state: { minimized: boolean; fullScreen: boolean }) => void; // Para notificar cambios de estado
  onPacienteChange?: (paciente: any) => void; // Para cambiar el paciente desde el padre
  aiGeneratedContent?: string;
  onAIGenerateComplete?: (content: string) => void;
  onAIPasteComplete?: () => void;
  windowState?: { minimized: boolean; fullScreen: boolean }; // NUEVO: para control global
  onGoToPacienteEmails?: (pacienteId: string) => void; // NUEVO: navegación global
  currentSelectedPaciente?: any | null; // NUEVO: paciente actual global
  emailsLoaded?: boolean; // NUEVO: indica si se han cargado emails
  isSwitchingPatient?: boolean; // NUEVO: indica si este modal está cambiando de paciente
}

// --- Utilidades para imágenes inline (copiado de EmailPreview) ---
const extractCidsFromHtml = (html: string): string[] => {
  if (!html) return [];
  const cidRegex = /src=[\"']cid:([^\"']+)[\"']/gi;
  const cids: string[] = [];
  let match;
  while ((match = cidRegex.exec(html)) !== null) {
    cids.push(match[1]);
  }
  return cids;
};

const replaceCidSrc = (html: string, email: any, detalle?: any): string => {
  if (!html || !email) return html;
  let newHtml = html;
  const cidsEncontrados = extractCidsFromHtml(html);
  // console.log('[DEBUG] replaceCidSrc - CIDs encontrados:', cidsEncontrados);
  // console.log('[DEBUG] replaceCidSrc - emailDetails:', detalle);
  
  if (cidsEncontrados.length > 0) {
    const attachments = detalle?.inline_attachments || [];
    // console.log('[DEBUG] replaceCidSrc - inline_attachments disponibles:', attachments);
    
    newHtml = newHtml.replace(/src=[\"']cid:([^\"']+)[\"']/gi, (match, cid) => {
      const cidNorm = cid.replace(/[<>\s]/g, '').toLowerCase();
      // console.log('[DEBUG] replaceCidSrc - procesando CID:', cid, 'normalizado:', cidNorm);
      
      const attachment = attachments.find((att: any) => {
        const attContentId = (att.contentId || '').replace(/[<>\s]/g, '').toLowerCase();
        const attName = (att.name || '').replace(/[<>\s]/g, '').toLowerCase();
        const matches = attContentId === cidNorm || attName === cidNorm;
        // console.log('[DEBUG] replaceCidSrc - comparando attachment:', { 
        //   attContentId, 
        //   attName, 
        //   cidNorm, 
        //   matches,
        //   hasToken: !!att.access_token 
        // });
        return matches;
      });
      
      if (attachment) {
        const token = attachment.access_token;
        const url = token 
          ? `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}?token=${token}`
          : `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}`;
        // console.log('[DEBUG] replaceCidSrc - URL generada:', url);
        return `src="${url}"`;
      }
      
      // console.log('[DEBUG] replaceCidSrc - no se encontró attachment para CID:', cid);
      return `src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200' preserveAspectRatio='xMidYMid meet'%3E%3Crect width='100%25' height='100%25' fill='%23f5f5f5' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' fill='%23888' font-style='italic' font-size='14' font-family='Arial, sans-serif'%3E%3Ctspan x='50%25' dy='-0.5em'%3ELa imagen no se%3C/tspan%3E%3Ctspan x='50%25' dy='1.2em'%3Eprocesó correctamente%3C/tspan%3E%3C/text%3E%3C/svg%3E" style="max-width: 300px; max-height: 200px; width: auto; height: auto;"`;
    });
  }
  return newHtml;
};

const TITLE_BAR_HEIGHT = 48; // px

const MinimizableComposeWindow: React.FC<MinimizableComposeWindowProps> = ({
  composeId,
  initialContext,
  onClose,
  onSendSuccess,
  zIndex,
  rightOffset,
  selectedPaciente,
  onWindowStateChange,
  onPacienteChange,
  aiGeneratedContent,
  onAIGenerateComplete,
  onAIPasteComplete,
  windowState = { minimized: false, fullScreen: false },
  onGoToPacienteEmails,
  currentSelectedPaciente,
  emailsLoaded = false,
  isSwitchingPatient = false
}) => {
  const theme = useTheme();
  const [showCc, setShowCc] = useState(false);
  
  // --- Toda la lógica de estado migrada del modal ---
  const [emailData, setEmailData] = useState<EmailPayload>({
    to: [],
    cc: [],
    subject: '',
    body: '',
  });
  
  // Ref para evitar problemas de stale state en closures
  const emailBodyRef = useRef(emailData.body);
  
  // Sincronizar la ref cada vez que el cuerpo cambia
  useEffect(() => {
    emailBodyRef.current = emailData.body;
  }, [emailData.body]);

  const [isSending, setIsSending] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isToEditable, setIsToEditable] = useState(false);
  const [isSubjectEditable, setIsSubjectEditable] = useState(false);

  // Nuevo estado para el diálogo
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDialogMinimized, setAiDialogMinimized] = useState(false);
  const [aiGenerationState, setAiGenerationState] = useState({ isGenerating: false, isCompleted: false });
  // ===== ESTADO CLAVE PARA EL FLUJO CORREGIDO =====
  // showCompletionTick indica que la generación está completa y el robotito debe mostrar
  // el tick verde. Este estado es el disparador para iniciar el flujo de revisión.
  const [showCompletionTick, setShowCompletionTick] = useState(false);
  // =================================================
  
  // Estado para el modal de confirmación de borrador
  const [showDraftConfirmation, setShowDraftConfirmation] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [modalPacienteOriginal, setModalPacienteOriginal] = useState<any | null>(null);
  const [generationEmailFilters, setGenerationEmailFilters] = useState<string[]>([]); // Emails del paciente desde donde se inició la generación

  // Estados para archivos adjuntos
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Referencias para cancelación de peticiones
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentRequestIdRef = useRef<number | null>(null);

  // ===== NUEVO: Estado para notificaciones (Snackbar) =====
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  } | null>(null);
  // =======================================================

  // --- Lógica de inicialización migrada del modal ---
  useEffect(() => {
    const { mode, email, prefilledTo, prefilledCc, originalMode, preparedBody, emailDetails } = initialContext;
    
    // Resetear estados
    setIsToEditable(false);
    setIsSubjectEditable(false);
    setShowCc(false);
    let newBody = '', newSubject = '', newTo: string[] = [], newCc: string[] = [];

    // CASO 1: Borradores (editar o enviar)
    if (originalMode === 'edit-draft' || originalMode === 'send-draft') {
      if (email) {
        newSubject = email.subject || '';
        // Procesar imágenes inline si hay emailDetails disponibles
        let rawBody = email.bodyHtml || email.body || email.bodyPreview || '';
        if (emailDetails && emailDetails.inline_attachments) {
          newBody = replaceCidSrc(rawBody, email, emailDetails);
        } else {
          // Mantener los CIDs originales para que el backend los procese correctamente
          newBody = rawBody;
        }
        if (email.to) newTo = email.to.map(r => r.email).filter(Boolean);
        if (email.cc && email.cc.length > 0) {
          newCc = email.cc.map(r => r.email).filter(Boolean);
          setShowCc(true);
        }
        if (originalMode === 'edit-draft') {
          setIsToEditable(true);
        }
      }
    }
    // CASO 2: Email nuevo (sin pre-rellenar o con pre-rellenado)
    else if (mode === 'new') {
      if (prefilledTo) {
        newTo = [prefilledTo];
      } else if (modalPacienteOriginal?.email) {
        // Auto-poblar con el email principal del paciente si no hay prefilledTo
        newTo = [modalPacienteOriginal.email];
      } else if (currentSelectedPaciente?.email) {
        // Fallback: usar el paciente actual del contexto global
        newTo = [currentSelectedPaciente.email];
      }
      if (prefilledCc) {
        newCc = [prefilledCc];
        setShowCc(true);
      }
      // Para emails nuevos, el asunto debe estar desbloqueado por defecto
      setIsSubjectEditable(true);
    }
    // CASO 3: Responder, responder a todos, o reenviar
    else if (email) {
      const originalSender = email.from?.email;
      const originalTo = email.to?.map(r => r.email) || [];
      const originalCc = email.cc?.map(r => r.email) || [];
      const drEmail = "drduran@hotmail.com.ar";
      
      // Usar la misma lógica que borradores para procesar el body
      let rawBody = email.bodyHtml || email.body || email.bodyPreview || '';
      let processedBody = rawBody;
      
      // Procesar imágenes inline si hay emailDetails disponibles
      if (emailDetails && emailDetails.inline_attachments) {
        // console.log('[DEBUG] Procesando imágenes inline en responder/reenviar:', emailDetails.inline_attachments);
        processedBody = replaceCidSrc(rawBody, email, emailDetails);
      } else {
        // console.log('[DEBUG] No hay emailDetails o inline_attachments en responder/reenviar:', { 
        //   hasEmailDetails: !!emailDetails, 
        //   hasInlineAttachments: !!(emailDetails?.inline_attachments),
        //   emailDetails: emailDetails 
        // });
        // Mantener los CIDs originales para que el backend los procese correctamente
        processedBody = rawBody;
      }
      // console.log('[DEBUG] processedBody antes de armar newBody:', processedBody);
      const formattedDate = new Date(email.receivedDateTime).toLocaleString('es-ES');
      const quoteHeader = `<p><br></p><hr><p><strong>De:</strong> ${email.from?.name} &lt;${email.from?.email}&gt;<br><strong>Enviado:</strong> ${formattedDate}<br><strong>Para:</strong> ${email.to?.map(t => t.email).join(', ')}<br><strong>Asunto:</strong> ${email.subject}</p>`;
      newBody = `${quoteHeader}<blockquote>${processedBody}</blockquote>`;

      switch (mode) {
        case 'reply':
          newSubject = `RE: ${email.subject}`;
          // Para emails enviados, no poner mi email en "Para"
          if (email.folder === 'enviados') {
            // En emails enviados, el destinatario original está en "to"
            newTo = originalTo.filter(e => e && e !== drEmail);
          } else {
            // Para emails recibidos, responder al remitente
            if (originalSender) newTo = [originalSender];
          }
          // isSubjectEditable se mantiene false por defecto
          break;
        case 'reply-all':
          newSubject = `RE: ${email.subject}`;
          const allRecipients = [originalSender, ...originalTo].filter(e => e && e !== drEmail);
          newTo = Array.from(new Set(allRecipients)).filter(Boolean);
          newCc = originalCc.filter(e => e !== drEmail);
          if (newCc.length > 0) setShowCc(true);
          // isSubjectEditable se mantiene false por defecto
          break;
        case 'forward':
          newSubject = `RV: ${email.subject}`;
          // Para reenviar, los destinatarios empiezan vacíos
          newTo = [];
          newCc = [];
          // SOLO para reenvío, habilitar el campo Para por defecto
          setIsToEditable(true);
          break;
      }
    }
    setEmailData({ to: newTo, cc: newCc, subject: newSubject, body: newBody });
    // console.log('[DEBUG] MinimizableComposeWindow - contenido final para RichTextEditor:', newBody);
  }, [initialContext]);

  // Obtener datos del paciente al inicializar
  useEffect(() => {
    // Si no hay selectedPaciente como prop, intentar obtenerlo del localStorage como fallback
    if (!selectedPaciente) {
      try {
        // console.log('[DEBUG FRONTEND] Verificando localStorage para paciente...');
        const pacienteSeleccionado = localStorage.getItem('selectedPacienteForEmail');
        // console.log('[DEBUG FRONTEND] Contenido del localStorage:', pacienteSeleccionado);
        
        if (pacienteSeleccionado) {
          const paciente = JSON.parse(pacienteSeleccionado);
          // console.log('[DEBUG FRONTEND] Paciente parseado del localStorage:', paciente);
          // No necesitamos setSelectedPaciente porque ahora viene como prop
        }
      } catch (error) {
        console.error('Error al parsear paciente desde localStorage:', error);
        localStorage.removeItem('selectedPacienteForEmail');
      }
    }
  }, [selectedPaciente]);

  // Solo fijar el paciente original del modal una vez al montar el compose
  useEffect(() => {
    if (!modalPacienteOriginal && currentSelectedPaciente) {
      // console.log('[DEBUG] Estableciendo modalPacienteOriginal:', currentSelectedPaciente);
      setModalPacienteOriginal(currentSelectedPaciente);
    }
    // eslint-disable-next-line
  }, [currentSelectedPaciente]);

  // Resetear estados cuando cambie el contexto inicial
  useEffect(() => {
    setShowCompletionTick(false);
    setAiGenerationState({ isGenerating: false, isCompleted: false });
  }, [initialContext.mode, initialContext.email?.id]);

  // Escuchar evento para expandir la ventana
  useEffect(() => {
    const handleExpandEvent = (event: CustomEvent) => {
      if (event.detail.composeId === composeId) {
        // Expandir al máximo la ventana
        onWindowStateChange?.(composeId, { minimized: false, fullScreen: true });
      }
    };

    const handleUnmaximizeEvent = (event: CustomEvent) => {
      if (event.detail.composeId === composeId) {
        // Desmaximizar la ventana
        onWindowStateChange?.(composeId, { minimized: false, fullScreen: false });
      }
    };

    const handleMinimizeEvent = (event: CustomEvent) => {
      if (event.detail.composeId === composeId) {
        // Minimizar la ventana
        onWindowStateChange?.(composeId, { minimized: true, fullScreen: false });
      }
    };

    window.addEventListener('expandComposeWindow', handleExpandEvent as EventListener);
    window.addEventListener('unmaximizeComposeWindow', handleUnmaximizeEvent as EventListener);
    window.addEventListener('minimizeComposeWindow', handleMinimizeEvent as EventListener);
    
    return () => {
      window.removeEventListener('expandComposeWindow', handleExpandEvent as EventListener);
      window.removeEventListener('unmaximizeComposeWindow', handleUnmaximizeEvent as EventListener);
      window.removeEventListener('minimizeComposeWindow', handleMinimizeEvent as EventListener);
    };
  }, [composeId]);

  // Pegar automáticamente el contenido generado por IA solo cuando se restaura desde minimizado
  useEffect(() => {
    // Solo pegar automáticamente si hay contenido generado, NO está minimizado, y el modal de IA se acaba de restaurar
    if (aiGeneratedContent && !windowState.minimized && !aiDialogMinimized && aiGenerationState.isCompleted) {
      setEmailData(prev => ({ ...prev, body: aiGeneratedContent }));
      if (onAIPasteComplete) onAIPasteComplete();
    }
  }, [aiGeneratedContent, windowState.minimized, aiDialogMinimized, aiGenerationState.isCompleted]);

  // --- Handlers migrados del modal ---
  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => { setEmailData(prev => ({ ...prev, subject: event.target.value })); };
  const handleToChange = (event: React.ChangeEvent<HTMLInputElement>) => { const emails = event.target.value.split(',').map(email => email.trim()); setEmailData(prev => ({ ...prev, to: emails }));};
  const handleCcChange = (event: React.ChangeEvent<HTMLInputElement>) => { const emails = event.target.value.split(',').map(email => email.trim()); setEmailData(prev => ({ ...prev, cc: emails })); };
  const handleBodyChange = (value: string) => { setEmailData(prev => ({ ...prev, body: value })); };
  const handleAttachFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateAI = () => {
    // Si el modal está cerrado, abrirlo
    if (!aiDialogOpen) {
      // Resetear tick de completado al iniciar nueva generación
      setShowCompletionTick(false);
      // Resetear estado de generación al iniciar nueva generación
      setAiGenerationState({ isGenerating: false, isCompleted: false });
      setAiDialogOpen(true);
      return;
    }
    
    // Si el modal está minimizado, restaurarlo a tamaño normal
    if (aiDialogMinimized) {
      setAiDialogMinimized(false);
    } else {
      // Si el modal está en tamaño normal, minimizarlo
      setAiDialogMinimized(true);
    }
  };

  const handleAiDialogClose = () => {
    console.log('[DEBUG] handleAiDialogClose llamado');
    setAiDialogOpen(false);
    setAiDialogMinimized(false);
    setShowCompletionTick(false);
    setAiGenerationState({ isGenerating: false, isCompleted: false });
    // Limpiar los emails capturados para la generación
    setGenerationEmailFilters([]);
  };

  const handleAiDialogMinimize = () => {
    setAiDialogMinimized(true);
  };

  const handleAiDialogRestore = () => {
    setAiDialogMinimized(false);
  };

  const handleAiDialogToggle = () => {
    // ===== INICIO DEL CAMBIO CLAVE =====
    // Si la generación está completada (el robotito tiene el tick verde),
    // el clic SIEMPRE debe abrir/restaurar el modal para iniciar el flujo de revisión.
    // Esto resuelve el problema de que los modales aparezcan prematuramente.
    //
    // FLUJO CORREGIDO:
    // 1. Usuario hace clic en robotito verde (showCompletionTick = true)
    // 2. Modal de IA se abre/restaura automáticamente
    // 3. useEffect en AIEmailGenerator detecta !isCurrentlyMinimized && isGenerationCompleted
    // 4. Se inicia el flujo de revisión (modales de peso/recordatorios)
    // 5. Solo cuando el usuario completa la revisión, se muestra el snackbar de éxito
    //
    if (showCompletionTick) {
      // Si el modal de IA no está abierto, o está minimizado, lo abrimos/restauramos.
      if (!aiDialogOpen || aiDialogMinimized) {
        setAiDialogOpen(true);
        setAiDialogMinimized(false);
      }
      // Si ya está abierto y no minimizado, no hacemos nada más.
      return; 
    }
    // ===== FIN DEL CAMBIO CLAVE =====

    // Lógica original para todos los demás casos (cuando no hay tick verde)
    if (aiDialogOpen) {
      if (aiDialogMinimized) {
        handleAiDialogRestore();
      } else {
        handleAiDialogClose();
      }
    } else {
      // Capturar los emails del paciente desde donde se inicia la generación
      // Usar modalPacienteOriginal si está disponible, sino currentSelectedPaciente
      const pacienteParaGeneracion = modalPacienteOriginal || currentSelectedPaciente;
      if (pacienteParaGeneracion) {
        const emailsPaciente = [
          pacienteParaGeneracion.email,
          pacienteParaGeneracion.email2
        ].filter(Boolean); // Filtrar emails vacíos
        setGenerationEmailFilters(emailsPaciente);
        // console.log('[DEBUG] Emails capturados para generación:', emailsPaciente);
      } else {
        // Fallback: usar los emails del campo 'to' si no hay paciente
        setGenerationEmailFilters(emailData.to.filter(Boolean));
        // console.log('[DEBUG] Usando emails del campo "to" como fallback:', emailData.to.filter(Boolean));
      }
      
      setAiDialogOpen(true);
    }
  };

  // ===== FUNCIÓN CLAVE PARA EL FLUJO CORREGIDO =====
  // Esta función se llama desde AIEmailGenerator cuando cambia el estado de generación.
  // Cuando la generación se completa, activa showCompletionTick para mostrar el tick verde.
  const handleAiGenerationStateChange = (state: { isGenerating: boolean; isCompleted: boolean }) => {
    setAiGenerationState(state);
    if (state.isCompleted && !state.isGenerating) {
      setShowCompletionTick(true);
    } else {
      setShowCompletionTick(false);
    }
  };
  // =================================================

  // ===== NUEVO: Handler para el botón de historial =====
  const handleRestoreLastAIResponse = async () => {
    if (!modalPacienteOriginal?.id) {
      setNotification({ open: true, message: 'No hay un paciente asociado a esta ventana.', severity: 'error' });
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/pacientes/${modalPacienteOriginal.id}/last-ai-response`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data && response.data.content) {
        // Aplicar el mismo procesamiento que se usa cuando se genera con IA
        // Usar la función applyGeneratedEmail para mantener consistencia
        applyGeneratedEmail(response.data.content, false); // false = no incluir plan por defecto
        
        setNotification({ open: true, message: 'Último mensaje de IA restaurado.', severity: 'success' });
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setNotification({ open: true, message: 'No se encontró un historial de IA para este paciente.', severity: 'info' });
      } else {
        console.error("Error fetching last AI response:", error);
        setNotification({ open: true, message: 'Error al recuperar el historial.', severity: 'error' });
      }
    }
  };
  // ====================================================

  const handleSwitchToModalPaciente = () => {
    console.log('[DEBUG] handleSwitchToModalPaciente llamado:', {
      modalPacienteOriginal: modalPacienteOriginal?.id,
      currentSelectedPaciente: currentSelectedPaciente?.id,
      onPacienteChange: !!onPacienteChange,
      onGoToPacienteEmails: !!onGoToPacienteEmails
    });
    
    if (modalPacienteOriginal && modalPacienteOriginal !== currentSelectedPaciente) {
      // Primero navegar a la pantalla de emails si no estamos ahí
      if (onGoToPacienteEmails && modalPacienteOriginal.id) {
        console.log('[DEBUG] Navegando a pantalla de emails');
        onGoToPacienteEmails(modalPacienteOriginal.id);
      }
      
      // Luego cambiar al paciente original del modal (esto abrirá el modal de días)
      if (onPacienteChange) {
        console.log('[DEBUG] Cambiando al paciente original del modal');
        onPacienteChange(modalPacienteOriginal);
      }
    }
  };

  // Nueva función para manejar el resultado de la generación
  const handleGenerationComplete = (result: { success: boolean; email: string }) => {
    try {
      console.log('Recibiendo resultado de generación completada:', result);
      
      // NO aplicar el email al editor inmediatamente aquí
      // El email se aplicará cuando se complete todo el flujo de modales desde AIEmailGenerator
      // Solo marcar que la generación está completa
      
      if (result.success && result.email) {
        console.log('Email generado exitosamente, esperando completar flujo de modales');
        // El email se aplicará al editor desde AIEmailGenerator cuando se complete el flujo
      } else {
        setError('Falló la generación del email con IA.');
      }
    } catch (err: any) {
      console.error('Error al procesar resultado de generación:', err);
      setError(err.message || 'Error al procesar el resultado de la generación.');
    }
  };

  // Nueva función para aplicar el email al editor cuando se complete el flujo de modales
  const applyGeneratedEmail = (email: string, incluirPlan: boolean = false) => {
    try {
      // Solo aplicar el email si la ventana no está minimizada
      if (!windowState.minimized) {
        console.log('Aplicando email generado al editor después de completar flujo de modales');
        
        // Preservar el HTML y solo convertir saltos de línea que no estén dentro de etiquetas HTML
        let formattedEmail = email;
        
        // Función para convertir saltos de línea preservando HTML
        const preserveHtmlAndConvertNewlines = (text: string) => {
          // Dividir por saltos de línea
          const lines = text.split('\n');
          const result = [];
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            // Si la línea contiene HTML, preservarla tal como está
            if (line.includes('<') && line.includes('>')) {
              result.push(line);
            } else {
              // Si es texto plano, convertir saltos de línea a <br />
              result.push(line);
            }
            
            // Añadir <br /> entre líneas (excepto después de la última)
            if (i < lines.length - 1) {
              result.push('<br />');
            }
          }
          
          return result.join('');
        };
        
        formattedEmail = preserveHtmlAndConvertNewlines(email);
        handleBodyChange(formattedEmail);
        
        // Si se incluyó el plan, actualizar el asunto a "Plan Alimentario"
        if (incluirPlan) {
          setEmailData(prev => ({ ...prev, subject: 'Plan Alimentario' }));
          console.log('Asunto actualizado a "Plan Alimentario" porque se incluyó el plan');
        }
        
        console.log('Email aplicado exitosamente al editor');
      } else {
        // Si está minimizado, esperar a que el usuario lo maximice para aplicar el email
        // (opcional: podrías guardar el email pendiente y aplicarlo al maximizar)
        console.log('El editor está minimizado, no se aplica el email generado aún.');
      }
    } catch (err: any) {
      console.error('Error al aplicar email al editor:', err);
      setError(err.message || 'Error al aplicar el email al editor.');
    }
  };

  // Nueva función para manejar la cancelación de la generación
  const handleGenerationCancelled = () => {
    // console.log('[DEBUG] handleGenerationCancelled llamado - Cerrando completamente el modal de IA');
    setError(null);
    // Resetear estado de generación cuando se cancela
    setAiGenerationState({ isGenerating: false, isCompleted: false });
    // Cerrar completamente el modal de IA cuando se cancela
    setAiDialogOpen(false);
    setAiDialogMinimized(false);
  };

  const handleSaveDraft = () => {
    // Mostrar modal de confirmación
    setShowDraftConfirmation(true);
  };

  const handleConfirmSaveDraft = async () => {
    setShowDraftConfirmation(false);
    setIsCreatingDraft(true);
    setError(null);
    try {
      // DEBUG: Imprime el estado JUSTO antes de enviarlo
      console.log("Guardando borrador con el siguiente cuerpo:", emailData.body);
      console.log("Usando ref para el cuerpo:", emailBodyRef.current);
      
      // Crear FormData para enviar archivos adjuntos
      const formData = new FormData();
      formData.append('to_recipients', JSON.stringify(emailData.to.filter(e => e.trim())));
      formData.append('cc_recipients', JSON.stringify(showCc ? emailData.cc?.filter(e => e.trim()) : []));
      formData.append('subject', emailData.subject);
      // ¡Usa la referencia para asegurarte de tener el último valor!
      formData.append('body', emailBodyRef.current);
      
      // Agregar archivos adjuntos
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
      formData.append('attachments_count', attachments.length.toString());
      
      await axios.post(`${API_BASE_URL}/save-draft-with-attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onSendSuccess();
      onClose(composeId);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al guardar el borrador';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const handleCancelSaveDraft = () => {
    setShowDraftConfirmation(false);
  };

  // Handler mejorado para el botón de edición
  const handleToggleEdit = () => {
    setIsToEditable(!isToEditable);
  };

  const handleToggleSubjectEdit = () => {
    setIsSubjectEditable(!isSubjectEditable);
  };

  const handleSend = async () => {
    if (!emailData.subject.trim()) { setError('El asunto es obligatorio'); return; }
    if (emailData.to.length === 0 || emailData.to.every(e => !e.trim())) { setError('Debe especificar al menos un destinatario en "Para"'); return; }
    setIsSending(true);
    setError(null);
    const bodyContent = typeof emailData.body === 'string' ? emailData.body : '';
    
    // Determinar el modo de envío
    let sendMode = initialContext.mode;
    let messageId = initialContext.email?.id;
    
    // Si es un borrador que se está enviando, usar el ID del borrador
    if (initialContext.originalMode === 'send-draft') {
      sendMode = 'new'; // Enviar como nuevo email
      messageId = initialContext.email?.id; // ID del borrador para eliminarlo después
    }
    
    try {
      // Crear FormData para enviar archivos adjuntos
      const formData = new FormData();
      formData.append('mode', sendMode);
      formData.append('originalMode', initialContext.originalMode || '');
      if (messageId) formData.append('message_id', messageId);
      formData.append('to_recipients', JSON.stringify(emailData.to.filter(e => e.trim())));
      // Para reenvíos, siempre enviar CC si está presente, sin importar showCc
      const ccToSend = initialContext.mode === 'forward' 
        ? (emailData.cc?.filter(e => e.trim()) || [])
        : (showCc ? emailData.cc?.filter(e => e.trim()) : []);
      formData.append('cc_recipients', JSON.stringify(ccToSend));
      formData.append('subject', emailData.subject);
      // Usar la ref para asegurar el último valor del cuerpo
      formData.append('body', emailBodyRef.current);
      
      // Agregar archivos adjuntos
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
      formData.append('attachments_count', attachments.length.toString());
      
      await axios.post(`${API_BASE_URL}/send-email-with-attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onSendSuccess();
      onClose(composeId); // Cierra esta ventana específica
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al enviar el email';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setIsSending(false);
    }
  };
  
  const getTitle = () => {
    const { originalMode, mode, email } = initialContext;
    
    // Manejar modos de borradores
    if (originalMode === 'edit-draft') {
      return 'Editar Borrador';
    } else if (originalMode === 'send-draft') {
      return 'Enviar Borrador';
    }
    
    // Para respuestas, incluir asunto y fecha del email original
    if ((mode === 'reply' || mode === 'reply-all' || mode === 'forward') && email) {
      const subject = email.subject || 'Sin asunto';
      const date = new Date(email.receivedDateTime).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      let action = '';
      switch(mode) {
        case 'reply': action = 'Responder'; break;
        case 'reply-all': action = 'Responder a Todos'; break;
        case 'forward': action = 'Reenviar'; break;
        default: action = 'Responder';
      }
      
      // Cortar el asunto a máximo 30 caracteres
      const shortSubject = subject.length > 30 ? subject.substring(0, 30) + '...' : subject;
      return `${action}: ${shortSubject} (${date})`;
    }
    
    // Para mensajes nuevos, incluir el asunto actual si se ha escrito
    if (mode === 'new') {
      const currentSubject = emailData.subject.trim();
      if (currentSubject) {
        // Cortar el asunto a máximo 30 caracteres
        const shortSubject = currentSubject.length > 30 ? currentSubject.substring(0, 30) + '...' : currentSubject;
        return `Nuevo Mensaje: ${shortSubject}`;
      }
      return 'Nuevo Mensaje';
    }
    
    // Modos normales
    switch(mode) {
      case 'reply': return 'Responder';
      case 'reply-all': return 'Responder a Todos';
      case 'forward': return 'Reenviar';
      default: return 'Nuevo Mensaje';
    }
  };

  const handleMinimizeToggle = () => {
    // Si está maximizada, minimizar completamente
    if (windowState.fullScreen) {
      onWindowStateChange?.(composeId, { minimized: true, fullScreen: false });
    } else if (windowState.minimized) {
      // Si está minimizada, ir a tamaño normal
      onWindowStateChange?.(composeId, { minimized: false, fullScreen: false });
    } else {
      // Si está en tamaño normal, minimizar
      onWindowStateChange?.(composeId, { minimized: true, fullScreen: false });
    }
  };

  const handleTitleBarClick = () => {
    // Si está maximizada, volver a tamaño normal
    if (windowState.fullScreen) {
      onWindowStateChange?.(composeId, { minimized: false, fullScreen: false });
    } else if (windowState.minimized) {
      // Si está minimizada, ir a tamaño normal
      onWindowStateChange?.(composeId, { minimized: false, fullScreen: false });
    } else {
      // Si está en tamaño normal, minimizar
      onWindowStateChange?.(composeId, { minimized: true, fullScreen: false });
    }
  };
  
  const handleFullscreenToggle = () => {
    // Cambiamos el estado de fullscreen.
    const newFullScreen = !windowState.fullScreen;
    // Siempre nos aseguramos que no esté minimizada al maximizar/desmaximizar.
    onWindowStateChange?.(composeId, { minimized: false, fullScreen: newFullScreen });
  };

  const handleCloseClick = () => onClose(composeId);

  // Handler para mostrar/ocultar CC con autopopulado inteligente
  const handleToggleCc = () => {
    setShowCc(!showCc);
    
    // Si se está activando CC y hay un paciente seleccionado, autopopular con el otro email del paciente
    if (!showCc && modalPacienteOriginal) {
      const pacienteEmails = [modalPacienteOriginal.email, modalPacienteOriginal.email2].filter(Boolean);
      
      // Obtener todos los emails que están actualmente en "Para:"
      const currentToEmails = emailData.to || [];
      
      // console.log('[DEBUG] handleToggleCc - Paciente emails:', pacienteEmails);
      // console.log('[DEBUG] handleToggleCc - Current To emails:', currentToEmails);
      
      // Encontrar el email del paciente que NO está en "Para:"
      const otherEmail = pacienteEmails.find(email => !currentToEmails.includes(email));
      
      // console.log('[DEBUG] handleToggleCc - Other email found:', otherEmail);
      
      if (otherEmail) {
        setEmailData(prev => ({ ...prev, cc: [otherEmail] }));
        // console.log('[DEBUG] handleToggleCc - CC autopopulated with:', otherEmail);
      }
    }
  };

  // Detectar si el paciente del modal es diferente al seleccionado globalmente
  const isPacienteMismatch = modalPacienteOriginal && 
    (currentSelectedPaciente === null || 
     modalPacienteOriginal.id !== currentSelectedPaciente.id ||
     !emailsLoaded);

  // NUEVA LÓGICA: Detectar si el modal corresponde al paciente actual basado en emails
  const isModalForCurrentPaciente = () => {
    if (!modalPacienteOriginal || !currentSelectedPaciente) {
      return false;
    }

    // Comparar IDs de pacientes
    if (modalPacienteOriginal.id === currentSelectedPaciente.id) {
      return true;
    }

    // Comparar emails (email y email2) entre el modal y el paciente actual
    const modalEmails = [
      modalPacienteOriginal.email?.toLowerCase().trim(),
      modalPacienteOriginal.email2?.toLowerCase().trim()
    ].filter(Boolean);

    const currentPacienteEmails = [
      currentSelectedPaciente.email?.toLowerCase().trim(),
      currentSelectedPaciente.email2?.toLowerCase().trim()
    ].filter(Boolean);

    // Verificar si hay coincidencia de emails
    return modalEmails.some(modalEmail => 
      currentPacienteEmails.includes(modalEmail)
    );
  };

  // Usar la nueva lógica para determinar el color del modal
  // Solo mostrar azul/habilitar si emailsLoaded es true
  const shouldShowBlueModal = emailsLoaded && isModalForCurrentPaciente();
  const shouldShowGreyModal = !shouldShowBlueModal && modalPacienteOriginal;

  // DEBUG: Log para ver qué está pasando con la detección de mismatch
  // console.log('[DEBUG] Modal mismatch detection:', {
  //   modalPacienteOriginal: modalPacienteOriginal?.id,
  //   currentSelectedPaciente: currentSelectedPaciente?.id,
  //   emailsLoaded,
  //   isPacienteMismatch,
  //   modalPacienteOriginalExists: !!modalPacienteOriginal,
  //   currentSelectedPacienteIsNull: currentSelectedPaciente === null,
  //   idsMatch: modalPacienteOriginal?.id === currentSelectedPaciente?.id,
  //   isModalForCurrentPaciente: isModalForCurrentPaciente(),
  //   shouldShowBlueModal,
  //   shouldShowGreyModal
  // });

  // Buscar el handler del botón verde del robotito (tick de generación completada)
  // Si el modal está minimizado, primero restaurar el modal antes de pegar el contenido generado
  const handleRobotTickClick = () => {
    // Solo restaurar el modal de IA si está minimizado, pero NO cambiar el estado de la ventana principal
    if (aiDialogMinimized) {
      // Restaurar el modal de compose si está minimizado
      if (windowState.minimized) {
        handleMinimizeToggle();
      }
      setAiDialogMinimized(false);
      // El useEffect de pegado se encargará de pegar el contenido generado cuando se restaure
    } else {
      // Si NO está minimizado (estado normal o maximizado), pegar el contenido generado directamente
      if (aiGeneratedContent) {
        setEmailData(prev => ({ ...prev, body: aiGeneratedContent }));
        if (onAIPasteComplete) onAIPasteComplete();
      }
    }
  };

  // Handler para ir a emails del paciente
  const handleGoToPacienteEmails = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onGoToPacienteEmails && modalPacienteOriginal?.id) {
      onGoToPacienteEmails(modalPacienteOriginal.id);
    }
  };

  // Log cuando cambien los emails de generación
  useEffect(() => {
    if (generationEmailFilters.length > 0) {
      // console.log('[DEBUG] Emails de generación actualizados:', {
      //   generationEmailFilters,
      //   modalPacienteOriginal: modalPacienteOriginal ? {
      //     id: modalPacienteOriginal.id,
      //     nombre: modalPacienteOriginal.nombre,
      //     email: modalPacienteOriginal.email,
      //     email2: modalPacienteOriginal.email2
      //   } : null,
      //   currentSelectedPaciente: currentSelectedPaciente ? {
      //     id: currentSelectedPaciente.id,
      //     nombre: currentSelectedPaciente.nombre,
      //     email: currentSelectedPaciente.email,
      //     email2: currentSelectedPaciente.email2
      //   } : null
      // });
    }
  }, [generationEmailFilters, modalPacienteOriginal, currentSelectedPaciente]);

  // Forzar re-render cuando cambien emailsLoaded o currentSelectedPaciente
  // Esto asegura que la barra del título se actualice correctamente
  // incluso si el modal está minimizado
  useEffect(() => {
    // Este useEffect no hace nada específico, pero fuerza el re-render
    // cuando cambian estas props críticas
  }, [emailsLoaded, currentSelectedPaciente?.id]);

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      <Box
        sx={{
          position: 'fixed',
          right: rightOffset,
          bottom: { xs: 8, sm: 16, md: 4 },
          zIndex: zIndex,
          width: windowState.minimized ? 240 : 600,
          minWidth: windowState.minimized ? 240 : 270,
          maxWidth: { xs: '98vw', sm: 500, md: 600 },
          height: windowState.fullScreen ?
            { xs: '85vh', sm: '88vh', md: '91.8vh' } :
            (windowState.minimized ? `${TITLE_BAR_HEIGHT}px` : '49vh'),
          transition: 'all 0.2s',
          display: 'flex',
          flexDirection: 'column',
          ...(windowState.minimized && {
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            height: `${TITLE_BAR_HEIGHT}px`,
            maxHeight: `${TITLE_BAR_HEIGHT}px`,
            p: 0,
            bgcolor: 'transparent',
          })
        }}
      >
        <Paper
          elevation={8}
          sx={{
            borderRadius: windowState.minimized ? 3 : 4,
            boxShadow: windowState.minimized ? 3 : 8,
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            minHeight: windowState.minimized ? `${TITLE_BAR_HEIGHT}px` : undefined,
            maxHeight: windowState.minimized ? `${TITLE_BAR_HEIGHT}px` : undefined,
            p: windowState.minimized ? 0 : undefined,
            bgcolor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Barra de título - siempre visible */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            backgroundColor: shouldShowBlueModal ? theme.palette.primary.main : shouldShowGreyModal ? 'grey.700' : theme.palette.primary.main, 
            color: 'white', 
            padding: windowState.minimized ? '0 6px' : '4px 8px',
            cursor: 'pointer',
            height: `${TITLE_BAR_HEIGHT}px`,
            minHeight: `${TITLE_BAR_HEIGHT}px`,
          }} onClick={handleTitleBarClick}>
            {/* Título flexible */}
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
                mr: 1
              }}
            >
              {getTitle()}
            </Typography>
            {/* Iconos intermedios (reciclaje, robot, tick) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {shouldShowGreyModal && (
                <Tooltip title={isSwitchingPatient ? `Cargando emails de ${modalPacienteOriginal?.nombre}...` : "Volver a este paciente"}>
                  {/* Contenedor para que el tamaño no cambie bruscamente */}
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, mr: 1 }}>
                    {isSwitchingPatient ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleSwitchToModalPaciente(); }} 
                        sx={{ color: 'white', backgroundColor: '#1976d2', borderRadius: '50%', '&:hover': { backgroundColor: '#1565c0' } }}
                      >
                        <PersonIcon fontSize="small" />
                        <RefreshIcon fontSize="small" sx={{ position: 'absolute', top: '-7px', right: '-4px', fontSize: 14, color: '#1976d2', backgroundColor: 'white', borderRadius: '50%', boxSizing: 'content-box' }} />
                      </IconButton>
                    )}
                  </Box>
                </Tooltip>
              )}
              {aiDialogMinimized && (
                <Tooltip title="Restaurar generación con IA">
                  <Box sx={{ position: 'relative', display: 'inline-block', mr: 1 }}>
                    <IconButton 
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); handleRobotTickClick(); }} 
                      sx={{ color: 'white', backgroundColor: aiGenerationState.isCompleted ? '#4caf50' : 'transparent', border: 'none', borderRadius: '50%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: aiGenerationState.isCompleted ? '#388e3c' : 'rgba(255, 255, 255, 0.1)' } }}
                    >
                      {aiGenerationState.isCompleted && !aiGenerationState.isGenerating && (
                        <span style={{ position: 'absolute', top: 'calc(50% - 13px)', left: 'calc(50% - 13px)', transform: 'translate(-50%, -50%)', width: 25, height: 25, borderRadius: '45%', boxShadow: '0 0 32px 16px #4caf50cc', zIndex: 0, pointerEvents: 'none', animation: 'pulse 1s infinite', background: 'transparent' }} />
                      )}
                      <RobotIcon fontSize="small" style={{ zIndex: 1 }} />
                      {aiGenerationState.isCompleted && !aiGenerationState.isGenerating && (
                        <CheckCircleIcon sx={{ position: 'absolute', top: '-4px', right: '-4px', color: 'white', zIndex: 2, backgroundColor: '#4caf50', borderRadius: '50%', fontSize: 16, boxSizing: 'content-box' }} />
                      )}
                    </IconButton>
                    {aiGenerationState.isGenerating && (
                      <CircularProgress size={12} sx={{ position: 'absolute', top: '-2px', right: '-4px', color: '#1976d2', zIndex: 2, backgroundColor: 'white', borderRadius: '50%', padding: '1px' }} />
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
            {/* Botones de acción */}
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              flexShrink: 0
            }}>
              <Tooltip title={windowState.minimized ? "Maximizar" : "Minimizar"}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMinimizeToggle();}} sx={{ color: 'white' }}>
                  <MinimizeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={windowState.fullScreen ? "Salir de pantalla completa" : "Pantalla completa"}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleFullscreenToggle();}} sx={{ color: 'white' }}>
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cerrar">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCloseClick();}} sx={{ color: 'white' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Contenido - solo visible cuando no está minimizado */}
          {!windowState.minimized && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              maxHeight: '100%',
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                px: { xs: 1.5, sm: 2 }, // Agrega padding horizontal
                pt: 1,
                pb: 0.5,
                mb: 1,
                bgcolor: 'transparent',
                borderRadius: 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 1, mb: 1 }}>
                  <TextField
                    variant="standard"
                    placeholder="Para"
                    fullWidth
                    value={emailData.to.join(', ')}
                    onChange={handleToChange}
                    InputProps={{
                      disableUnderline: true,
                      readOnly: !isToEditable,
                      startAdornment: <InputAdornment position="start">Para:</InputAdornment>,
                      endAdornment: (
                        <Tooltip title="Editar destinatarios">
                          <IconButton size="small" onClick={handleToggleEdit} sx={{ color: isToEditable ? 'primary.main' : 'text.secondary' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                      sx: {
                        color: 'text.primary',
                        backgroundColor: isToEditable ? 'white' : 'transparent',
                        borderRadius: isToEditable ? '4px' : '0px',
                        padding: isToEditable ? '4px 8px' : '0px',
                        border: isToEditable ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                          fontWeight: 'normal',
                          cursor: isToEditable ? 'text' : 'default',
                        }
                      }
                    }}
                  />
                  <Button
                    size="small"
                    onClick={handleToggleCc}
                    sx={{
                      ml: 1,
                      color: showCc ? 'primary.main' : 'text.secondary',
                      fontWeight: showCc ? 'bold' : 'normal'
                    }}
                  >
                    CC
                  </Button>
                </Box>

                {showCc && (
                  <TextField
                    variant="standard"
                    placeholder="CC"
                    fullWidth
                    value={emailData.cc?.join(', ')}
                    onChange={handleCcChange}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: <InputAdornment position="start">CC:</InputAdornment>
                    }}
                    sx={{
                      mb: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      pb: 1,
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                          fontWeight: 'normal',
                          cursor: 'text',
                        }
                      }
                    }}
                  />
                )}

                <TextField
                  variant="standard"
                  placeholder="Asunto"
                  fullWidth
                  value={emailData.subject}
                  onChange={handleSubjectChange}
                  InputProps={{
                    disableUnderline: true,
                    readOnly: !isSubjectEditable,
                    startAdornment: <InputAdornment position="start">Asunto:</InputAdornment>,
                    endAdornment: (
                      <Tooltip title="Editar asunto">
                        <IconButton size="small" onClick={handleToggleSubjectEdit} sx={{ color: isSubjectEditable ? 'primary.main' : 'text.secondary' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                    sx: {
                      color: 'text.primary',
                      backgroundColor: isSubjectEditable ? 'white' : 'transparent',
                      borderRadius: isSubjectEditable ? '4px' : '0px',
                      padding: isSubjectEditable ? '4px 8px' : '0px',
                      border: isSubjectEditable ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                        fontWeight: 'normal',
                        cursor: isSubjectEditable ? 'text' : 'default',
                      }
                    }
                  }}
                  sx={{
                    borderBottom: 0,
                    borderColor: 'divider',
                    pb: 0
                  }}
                />
              </Box>
              
              {/* Lista de archivos adjuntos */}
              {attachments.length > 0 && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Archivos adjuntos ({attachments.length}):
                  </Typography>
                  {attachments.map((file, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 0.5,
                      mb: 0.5,
                      bgcolor: 'white',
                      borderRadius: 0.5,
                      border: '1px solid #e0e0e0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <AttachFileIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({(file.size / 1024).toFixed(1)} KB)
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveAttachment(index)}
                        sx={{ ml: 1, p: 0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Box sx={{
                flex: 1,
                my: 1,
                minHeight: 0,
                px: { xs: 1, sm: 2 },
                // La altura de la barra de botones es de aprox. 48px-56px.
                // Un padding de 4.5 unidades de tema (36px) es el punto medio ideal.
                pb: 4.5, 
              }}>
                <RichTextEditor value={emailData.body} onChange={handleBodyChange} />
              </Box>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}
              
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 1,
                pb: { xs: 0.5, sm: 1 },
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
                bgcolor: 'grey.100',
                zIndex: 2,
                borderTop: '1px solid #eee',
                px: { xs: 1, sm: 2 },
              }}>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                      <Button 
                          variant="contained" 
                          startIcon={<SendIcon />} 
                          onClick={handleSend} 
                          disabled={shouldShowGreyModal || isSending}
                          size="small"
                          sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' } as any,
                              px: { xs: 1, sm: 2 }
                          }}
                      >
                          {isSending ? 'Enviando...' : 'Enviar'}
                      </Button>

                      {/* Ocultar "Crear Borrador" si estamos editando o enviando un borrador */}
                      {initialContext.originalMode !== 'edit-draft' && initialContext.originalMode !== 'send-draft' && (
                        <Button
                          onClick={handleSaveDraft}
                          startIcon={<SaveIcon />}
                          disabled={shouldShowGreyModal || isSending || isCreatingDraft}
                          sx={{ 
                            ml: 1, 
                            backgroundColor: 'grey.200', 
                            color: 'black',
                            '&:hover': { backgroundColor: 'grey.300' } 
                          }}
                        >
                          Crear Borrador
                          {isCreatingDraft && <CircularProgress size={20} sx={{ ml: 1 }} />}
                        </Button>
                      )}
                      
                      {/* ===== NUEVO: Botón de Historial ===== */}
                      <Tooltip title="Restaurar último mensaje de IA">
                        <span> {/* Span para que el Tooltip funcione con botón deshabilitado */}
                          <IconButton 
                            onClick={handleRestoreLastAIResponse} 
                            disabled={shouldShowGreyModal || isSending || isCreatingDraft}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/* ======================================= */}
                  </Box>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                      <Tooltip title="Generar con IA">
                          <Box sx={{ position: 'relative', display: 'inline-block' }}>
                              <IconButton onClick={handleAiDialogToggle} color="primary" disabled={shouldShowGreyModal}>
                                  <AIIcon sx={{ color: shouldShowGreyModal ? '#bdbdbd' : '#1565c0' }} />
                              </IconButton>
                              {/* Loading azul cuando está generando */}
                              {aiGenerationState.isGenerating && (
                                  <CircularProgress
                                      size={14}
                                      sx={{
                                          position: 'absolute',
                                          top: '0px',
                                          right: '0px',
                                          color: 'white',
                                          zIndex: 2,
                                          backgroundColor: '#1565c0',
                                          borderRadius: '50%',
                                          padding: '1px',
                                      }}
                                  />
                              )}
                              {/* TICK DEL BOTÓN DE IA EN CONTENIDO DEL MODAL - Azul oscuro sin contorno */}
                              {showCompletionTick && (
                                  <CheckCircleIcon
                                      sx={{
                                          position: 'absolute',
                                          top: '0px',
                                          right: '0px',
                                          color: 'white',
                                          zIndex: 2,
                                          backgroundColor: '#1565c0',
                                          borderRadius: '50%',
                                          fontSize: 14,
                                          boxSizing: 'content-box'
                                      }}
                                  />
                              )}
                          </Box>
                      </Tooltip>
                      <Tooltip title="Adjuntar archivo">
                          <IconButton onClick={handleAttachFile} size="small">
                              <AttachFileIcon fontSize="small" />
                          </IconButton>
                      </Tooltip>
                      <Tooltip title="Descartar">
                          <IconButton onClick={handleCloseClick} size="small">
                              <DeleteIcon fontSize="small" />
                          </IconButton>
                      </Tooltip>
                  </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* El diálogo de IA se renderiza aquí */}
      <AIEmailGenerator
        open={aiDialogOpen}
        onClose={handleAiDialogClose}
        onGenerationComplete={handleGenerationComplete}
        onGenerationCancelled={handleGenerationCancelled}
        onApplyEmail={applyGeneratedEmail}
        selectedMessage={initialContext.email || null}
        emailFilters={generationEmailFilters}
        pacienteData={currentSelectedPaciente}
        modalPacienteOriginal={modalPacienteOriginal}
        generationId={generationId || undefined}
        setGenerationId={setGenerationId}
        isMinimized={aiDialogMinimized}
        onMinimize={handleAiDialogMinimize}
        onRestore={handleAiDialogRestore}
        composeId={composeId}
        onGenerationStateChange={handleAiGenerationStateChange}
      />

      {/* Input oculto para selección de archivos */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Modal de confirmación para crear borrador */}
      <Dialog
        open={showDraftConfirmation}
        onClose={handleCancelSaveDraft}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 9999, // Asegurar que esté por encima de todo
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1
        }}>
          <SaveIcon sx={{ color: '#1976d2', fontSize: 24 }} />
          <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
            Crear Borrador
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
            ¿Estás seguro que deseas crear un borrador con el contenido actual?
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1, 
            border: '1px solid #e0e0e0',
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              Detalles del borrador:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Para:</strong> {emailData.to.filter(e => e.trim()).join(', ') || 'Sin destinatarios'}
              </Typography>
              {showCc && emailData.cc && emailData.cc.length > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>CC:</strong> {emailData.cc.filter(e => e.trim()).join(', ')}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Asunto:</strong> {emailData.subject || 'Sin asunto'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Archivos adjuntos:</strong> {attachments.length} archivo(s)
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={handleCancelSaveDraft}
            variant="outlined"
            sx={{ 
              px: 3,
              '&:hover': { backgroundColor: 'grey.100' }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSaveDraft}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreatingDraft}
            sx={{ 
              px: 3,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            {isCreatingDraft ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creando...
              </>
            ) : (
              'Crear Borrador'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* En el render, agregar un botón/avatar de paciente que llame a handleGoToPacienteEmails */}
      {modalPacienteOriginal && (
        <Tooltip title={`Ver emails de ${modalPacienteOriginal.nombre || 'paciente'}`}>
          <Box sx={{ position: 'relative', display: 'inline-block', mt: '0px', ml: 1, mr: 1 }}>
            <IconButton
              size="small"
              onClick={handleGoToPacienteEmails}
              sx={{
                color: 'white',
                backgroundColor: '#1976d2',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      )}

      {/* ===== NUEVO: Snackbar para notificaciones ===== */}
      <Snackbar
        open={!!notification}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
      {/* ============================================= */}
    </>
  );
};

export default MinimizableComposeWindow;

// =====================================================================================
// ===== RESUMEN DE LA IMPLEMENTACIÓN ==================================================
// =====================================================================================
// 
// CAMBIOS IMPLEMENTADOS:
// 
// 1. AIEmailGenerator.tsx:
//    - Nuevo estado pendingReviewData para almacenar datos pendientes de revisión
//    - Función startReviewFlow() para iniciar el flujo de modales
//    - useEffect modificado para NO mostrar modales automáticamente
//    - Nuevo useEffect que se dispara solo cuando el modal se abre/restaura
// 
// 2. MinimizableComposeWindow.tsx:
//    - handleAiDialogToggle() modificado para detectar showCompletionTick
//    - Cuando showCompletionTick es true, SIEMPRE abre/restaura el modal
//    - showCompletionTick es el único disparador para el flujo de revisión
// 
// RESULTADO:
// - Los modales NO aparecen automáticamente cuando termina la generación
// - El usuario debe hacer clic en el robotito verde para iniciar el flujo
// - La generación puede completarse en segundo plano sin problemas
// - El flujo se completa correctamente cuando el usuario interactúa
// 
// ===================================================================================== 